type EventType = 'error' | 'change';
type EventHandler = (message: string | File[]) => void;

class FileUploader {
  readonly files: Set<File>;
  private readonly fileInput: HTMLInputElement | null;
  private readonly maxFiles: number;
  private readonly maxSizeInMB: number;
  private readonly element: HTMLElement;
  private readonly fileList: HTMLElement | null;
  private readonly label: HTMLElement | null;

  private readonly eventHandlers: Map<EventType, Set<EventHandler>>;

  constructor(
    element: HTMLElement,
    options = {
      maxFiles: 4,
      maxSizeInMB: 10,
    },
  ) {
    this.maxFiles = options.maxFiles;
    this.maxSizeInMB = options.maxSizeInMB;
    this.files = new Set();
    this.element = element;
    this.eventHandlers = new Map();

    this.fileInput = this.element.querySelector('input[type="file"]');
    this.fileList = this.element.querySelector('.file-upload__list');
    this.label = this.element.querySelector('.file-upload__label');

    if (!this.fileInput || !this.fileList) {
      throw new Error('File input or file list not found');
    }

    this.init();
  }

  addEventListener(type: EventType, handler: EventHandler): void {
    if (!this.eventHandlers.has(type)) {
      this.eventHandlers.set(type, new Set());
    }
    this.eventHandlers.get(type)?.add(handler);
  }

  removeEventListener(type: EventType, handler: EventHandler): void {
    this.eventHandlers.get(type)?.delete(handler);
  }

  init() {
    this.fileInput?.addEventListener('change', (e) => this.handleFileSelect(e));
  }

  handleFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    const newFiles = Array.from(target.files || []);

    if (newFiles.length + this.files.size > this.maxFiles) {
      this.emitEvent('error', `Maximum ${this.maxFiles} fichiers autorisés`);
      return;
    }

    newFiles.forEach((file) => {
      if (file.size > this.maxSizeInMB * 1024 * 1024) {
        this.emitEvent('error', `Taille maximum autorisée: ${this.maxSizeInMB}MB`);
        return;
      }

      this.files.add(file);
      this.renderFile(file);
    });

    this.emitEvent('change', Array.from(this.files));
    this.checkLength();

    target.value = '';
  }

  renderFile(file: File) {
    const template = this.element.querySelector<HTMLTemplateElement>('.file-upload-list-item-template');
    if (!template) {
      throw new Error('Template not found');
    }

    const clone = template.content.cloneNode(true) as DocumentFragment;
    const fileElement = clone.firstElementChild as HTMLElement;

    const fileNameElement = fileElement.querySelector('[data-filename]') as HTMLElement;
    fileNameElement.textContent = file.name;

    const deleteBtn = fileElement.querySelector('.file-upload__list__item__delete-btn');
    deleteBtn?.addEventListener('click', () => {
      this.files.delete(file);
      fileElement.remove();
      this.emitEvent('change', Array.from(this.files));
      this.checkLength();
    });

    this.fileList?.appendChild(fileElement);
  }

  checkLength = () => {
    if (this.files.size === this.maxFiles) {
      this.label?.classList.add('is-disabled');
      this.fileInput?.setAttribute('disabled', 'disabled');
    } else {
      this.label?.classList.remove('is-disabled');
      this.fileInput?.removeAttribute('disabled');
    }
  };

  getFiles = () => Array.from(this.files);

  private emitEvent(type: EventType, message: string | File[]): void {
    this.eventHandlers.get(type)?.forEach((handler) => handler(message));
  }
}

export default FileUploader;
